<template>
    <b-row class="mb-3">
        <b-card no-body bg-variant="white" class="w-100">
            <template #header>
                <div class="d-flex align-items-center justify-content-between">
                    <h5 class="mb-0">스키마 입력 값 검색하기</h5>

                    <div v-if="loading">
                        <div class="d-flex align-items-center" style="height: 36px">
                            <b-spinner class="mr-2" variant="primary" style="height: 1rem; width: 1rem;"/>
                            <p class="d-inline-block mb-0">조회중...</p>
                        </div>
                    </div>
                    <div v-else class="d-flex align-items-center">
                        <b-button form="schema-input-search-form"
                                  type="reset"
                                  :disabled="loading"
                        >
                            {{ $t('초기화') }}
                        </b-button>
                    </div>
                </div>
            </template>

            <div class="p-3">
                <b-form id="schema-input-search-form"
                        autocomplete="new-password"
                        @submit.stop.prevent="searchData"
                        @reset="resetSearch"
                >
                    <b-row>
                        <b-col cols="12" class="mb-3 mb-xl-0">
                            <b-form-group
                                id="fieldset-input-project_id"
                                label="프로젝트"
                                label-for="input-project_id"
                                class="mb-0"
                            >
                                <div class="d-flex align-items-center rounded text-secondary w-100">
                                    <auto-complete-select ref="projectCodeAutoComplete"
                                                          v-model="selectedProjectCodeId"
                                                          :options="projectCodeOptions"
                                                          :loading="loading"
                                                          start-expanded
                                                          list-max-height="360px"
                                                          option-value-key="id"
                                                          :placeholder="$t('프로젝트를 선택해주세요.')"
                                                          autocomplete="new-password"
                                                          @click="onClickProjectSelect"
                                    >
                                    </auto-complete-select>
                                </div>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-form>

            </div>
        </b-card>


        <b-row class="mt-3">
            <b-col cols="12">
                <b-card
                    :class="[{ 'shadow-sm': searchResults || (!loading && searchResults) }, 'list-container']"
                    :bg-variant="!searchResults ? 'light' : 'white'"
                    no-body
                >
                    <template #header>
                        <div class="d-flex align-items-center justify-content-between">
                            <div>
                                <h6 class="mb-0">스키마 입력 값 목록</h6>
                            </div>
                            <div class="d-flex align-items-center">
                                <b-button variant="outline-secondary" size="md"
                                          class="mr-2"
                                          @click="openSchemaInputChangeOrderModal">
                                    스키마 입력 값 순서변경
                                </b-button>
                                <b-button variant="outline-primary" size="md"
                                          @click="openSchemaInputModal('add')">
                                    스키마 입력 값 추가
                                </b-button>
                            </div>
                        </div>
                    </template>
                    <div class="table-container">
                        <b-table
                            fixed responsive
                            :items="searchResults"
                            :fields="tableColumns"
                            class="mb-0"
                            show-empty
                            :busy="loading"
                        >
                            <template #empty>
                                <PageEmptyComponent
                                    :text="$t('조회된 스키마 입력 값가 없습니다.')"
                                    :loading="loading"
                                    :loadingText="$t('스키마 입력 값 목록을 조회 중입니다.')"
                                />
                            </template>
                            <template #cell(project_id)="row">
                                <span v-b-tooltip.hover="get_tooltip_title(getTargetProjectText(row.value))">
                                    {{ getTargetProjectText(row.value) }}
                                </span>
                            </template>

                            <template #cell(key_name)="row">
                                <span v-b-tooltip.hover="get_tooltip_title(row.value)">{{ row.value }}</span>
                            </template>
                            <template #cell(value_type)="row">
                                <b-badge variant="dark" class="f-80">
                                    {{ getValueTypeText(row.value) }}
                                </b-badge>
                            </template>
                            <template #cell(label_key)="row">
                                <span v-b-tooltip.hover="get_tooltip_title(row.value)" class="text-link">
                                    {{ row.value }}
                                </span>
                            </template>

                            <template #cell(is_required)="row">
                                <b-badge :variant="row.value ? 'success' : null" class="f-80">
                                    {{ getRequiredName(row.value) }}
                                </b-badge>
                            </template>

                            <template #cell(detail)="data">
                                <b-btn size="sm"
                                       variant="outline-primary"
                                       @click="openSchemaInputModal('detail', data.item)">상세
                                </b-btn>
                                <b-btn size="sm"
                                       variant="outline-secondary"
                                       class="ml-1"
                                       @click="openSchemaInputCopy(data.item)">복사
                                </b-btn>
                                <b-btn size="sm"
                                       variant="outline-danger"
                                       class="ml-1"
                                       @click="deleteSchemaInput(data.item)">삭제
                                </b-btn>
                            </template>

                            <template #cell(updated_at)="row">
                                {{ get_date(row.value) }}

                            </template>
                            <template #cell(created_at)="row">
                                {{ get_date(row.value) }}
                            </template>

                        </b-table>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <schema-input-modal ref="schemaInputModal"
                            :detail-id="detailId"
                            :selectedProjectCodeId="selectedProjectCodeId"
                            :projects="projects"
                            @updateDetailData="resetPage"
                            @addData="resetPage"
                            @deleteData="resetPage"
        />
        <schema-input-order-change-modal :project-text="getTargetProjectText(selectedProjectCodeId)"
                                         :schema-inputs="searchResults"
                                         @schemaInputsOrderChanged="resetPage"
        />

    </b-row>
</template>

<script>
import {listInputKeys} from '@shared_utils/api/projectSchema';
import {
    getProjectCodeOptions,
    getTargetProjectText,
    onTooltipShow
} from '@shared/components/admin/pages/Management/common-data';
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import PaginationPageMixin from '@shared/mixin/pagination-page-mixins';
import {columns, valueTypeOptions} from '@shared/components/admin/pages/Management/ProjectSchema/SchemaInput/data';
import AutoCompleteSelect from '@core/components/common/AutoCompleteSelect.vue';
import SchemaInputModal
    from '@shared/components/admin/pages/Management/ProjectSchema/SchemaInput/SchemaInputModal.vue';
import SchemaInputOrderChangeModal
    from '@shared/components/admin/pages/Management/ProjectSchema/SchemaInput/SchemaInputOrderChangeModal.vue';

export default {
    name: 'ManageSchemaInput',
    components: {
        SchemaInputOrderChangeModal,
        SchemaInputModal,
        AutoCompleteSelect,
        PageEmptyComponent,
    },
    mixins: [PaginationPageMixin({searchForm: {project_id: null}, pagination: null})],
    props: {
        currentTab: {
            type: Number,
            default: 0
        }
    },
    data() {
        return {
            selectedProjectCodeId: null,
            projects: null,

            tableColumns: columns,
            detailId: null,
            dataLoading: false,
        };
    },
    computed: {
        loading() {
            return this.listLoading || this.dataLoading;
        },
        getTargetProjectText() {
            return (project_id) => getTargetProjectText(project_id, this.projects);
        },
        getRequiredName() {
            return (is_required) => {
                return is_required ? this.$t('필수') : this.$t('선택');
            };
        },
        projectCodeOptions() {
            return getProjectCodeOptions(this.projects);
        },
        getValueTypeText() {
            return (value_type) => {
                return valueTypeOptions.find((item) => item.value === value_type)?.text ?? '-';
            };
        },
    },
    watch: {
        '$route': {
            async handler() {
                if (this.$route.query.tab === 'schemaInput') {

                    this.setSearchDataFormQuery();
                    this.selectedProjectCodeId = Number(this.searchFormValue.project_id);
                    await this.fetchData();
                }
            }
        }
    },
    async mounted() {
        if (this.$route.query.tab !== 'schemaInput') return;

        await this.initPage();
    },
    methods: {
        onTooltipShow,
        async initPage() {
            this.dataLoading = true;

            this.setSearchDataFormQuery();
            this.selectedProjectCodeId = this.searchFormValue.project_id;

            try {
                await Promise.all([
                    this.fetchData(),
                    this.getProjectCode(),
                ]);
                setTimeout(() => {
                    this.$refs.projectCodeAutoComplete.expandAllGroups();
                }, 300);
            } catch (e) {
                console.error(e);
            } finally {
                this.dataLoading = false;
            }
        },

        async getProjectCode() {
            try {
                const response = await this.$axios.get('/project_code');
                this.projects = response.data;
            } catch (error) {
                alert('죄송합니다. 프로젝트 코드 데이터를 불러오는 도중 장애라 발생했습니다. 개발팀에게 문의주시기 바랍니다.');
            }
        },
        async searchData() {
            this.searchFormValue.project_id = this.selectedProjectCodeId;
            const checkSameQuery = await this.changeQuery({});
            if (checkSameQuery) {
                await this.fetchData();
            }
        },
        resetPage() {
            this.setSearchDataFormQuery();
            this.fetchData();
        },
        async resetSearch(){
            this.selectedProjectCodeId = null;
            this.searchFormValue.project_id = null;
            const checkSameQuery = await this.changeQuery({});
            if (checkSameQuery) {
                await this.fetchData();
            }
        },
        async fetchData() {
            try {
                this.listLoading = true;

                const res = await listInputKeys({
                    params: {
                        project_id: this.selectedProjectCodeId,
                    },
                });

                if (res.data == null) {
                    this.$EventBus.$emit('toast-success', this.$t('검색 완료'), '스키마 입력 값 검색 결과가 없습니다.');
                    return;
                }
                const {input_keys, total} = res.data;

                this.searchResults = input_keys.map((item, index) => {
                    const data = {
                        ...item,
                        value: JSON.stringify(item.value),
                        index: total - index,
                    };
                    return data;
                });
            } catch (error) {
                this.$EventBus.$emit('toast-failure', this.$t('검색 오류'), '스키마 입력 값 검색 도중 오류가 발생했습니다. 잠시후 재시도 해주세요.');
            } finally {
                this.listLoading = false;
            }
        },
        openSchemaInputCopy(item) {
            this.detailId = null;
            this.$bvModal.show('schema-input-detail-modal');
            this.$refs.schemaInputModal.openAsCopy(item.id);
        },
        // 리스트에서 바로 삭제. 기존엔 상세→수정 모드로 들어가야만 삭제 버튼이 보여
        // 발견성이 낮았다. 모달의 삭제와 동일한 confirm + DELETE API 를 재사용한다.
        deleteSchemaInput(item) {
            this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: this.$t('스키마 입력 값 삭제'),
                message: this.$t('현 스키마 입력 값을 삭제하시겠습니까?')
            }).then((confirmed) => {
                if (!confirmed) return;
                this.$axios.delete(`/project-schema/input-keys/${item.id}`)
                    .then(() => {
                        this.$EventBus.$emit('toast-success', this.$t('스키마 입력 값 삭제'), this.$t('스키마 입력 값가 성공적으로 삭제되었습니다.'));
                        this.resetPage();
                    })
                    .catch((error) => {
                        console.error(error);
                        this.$EventBus.$emit('toast-failure', this.$t('스키마 입력 값 삭제'), this.$t('스키마 입력 값 삭제 중 오류가 발생했습니다. 개발 팀에 문의해주세요.'));
                    });
            });
        },
        async openSchemaInputModal(mode, item) {
            if (mode === 'add') {
                this.detailId = null;
                this.$refs.schemaInputModal.changeMode(mode);
                this.$bvModal.show('schema-input-detail-modal');
                return;
            }

            this.detailId = item.id;
            this.$refs.schemaInputModal.changeMode(mode);
            this.$bvModal.show('schema-input-detail-modal');
        },
        openSchemaInputChangeOrderModal(){
            if(this.selectedProjectCodeId == null){
                this.$showModal({
                    type: 'alert',
                    state: 'warning',
                    title: this.$t('프로젝트 선택 필요'),
                    message: this.$t('스키마 입력 값 순서 변경을 위해서는 먼저 프로젝트를 선택해야 합니다.')
                });
                return;
            }
            this.$bvModal.show('schema-input-order-change-modal');
        },
        onClickProjectSelect(){
            setTimeout(()=>{
                this.searchData();
            },100);
        }
    }
};
</script>

<style lang="scss">
</style>
